/**
 * The deal as the customer may see it: what they're handing over and taking, each at the price
 * agreed, and who owes what. No market values and no %, so the phone can be turned around at the
 * counter, or the page printed for them to take away. Printing shows only this sheet.
 */
import { createPortal } from 'react-dom'
import { Printer, X } from 'lucide-react'
import { hasCardNumber } from '../../../utils/cardNumber'
import type { TradeItem, TradeType } from './types'
import styles from '../CreateTransaction.module.css'

function Lines({ title, items, total, formatPrice }: {
  title: string
  items: TradeItem[]
  total: number
  formatPrice: (cents: number) => string
}) {
  if (!items.length) return null
  return (
    <section className={styles.customerViewSection}>
      <h3>{title}</h3>
      <ul>
        {items.map((item) => (
          <li key={item.id}>
            <span>
              <b>{item.quantity > 1 ? `${item.quantity} × ` : ''}{item.itemName}</b>
              <small>
                {[item.setName, hasCardNumber(item.cardNumber) && !item.itemName.includes(item.cardNumber) ? `#${item.cardNumber}` : null, item.condition].filter(Boolean).join(' · ')}
              </small>
            </span>
            <span className={styles.customerViewPrice}>{formatPrice(item.totalValueCents)}</span>
          </li>
        ))}
      </ul>
      <p className={styles.customerViewSubtotal}><span>Total</span><b>{formatPrice(total)}</b></p>
    </section>
  )
}

export function CustomerViewSheet({ trade, storeName, formatPrice, onClose }: {
  trade: TradeType
  storeName: string
  formatPrice: (cents: number) => string
  onClose: () => void
}) {
  const diff = trade.differenceValueCents
  return createPortal(
    <div className={styles.customerView} role="dialog" aria-modal="true" aria-label="Customer view">
      <div className={styles.customerViewPage}>
        <header className={styles.customerViewHead}>
          <div>
            <h2>{storeName}</h2>
            <p>{trade.customerName ? `${trade.customerName} · ` : ''}{new Date().toLocaleDateString()}</p>
          </div>
          <div className={styles.customerViewActions}>
            <button type="button" onClick={() => window.print()} aria-label="Print"><Printer size={18} aria-hidden /></button>
            <button type="button" onClick={onClose} aria-label="Close"><X size={18} aria-hidden /></button>
          </div>
        </header>
        <Lines title="You're handing over" items={trade.customerItems ?? []} total={trade.customerValueCents} formatPrice={formatPrice} />
        <Lines title="You're taking" items={trade.storeItems ?? []} total={trade.storeValueCents} formatPrice={formatPrice} />
        <p className={styles.customerViewResult}>
          {diff === 0 ? <b>Even trade</b> : <><span>{diff > 0 ? 'You pay' : 'We pay you'}</span><b>{formatPrice(Math.abs(diff))}</b></>}
        </p>
      </div>
    </div>,
    document.body,
  )
}
